import { useEffect, useState } from 'react';
import { useNavigation } from '../shared/hooks/useNavigation';
import { ROUTES } from '../shared/navigation';
import BottomNavigation from '../components/BottomNavigation';
import { useAuthStore } from '../stores/authStore';
import { getUserProfile } from '../services/userService';
import { User, Mail, Phone, LogOut, Settings, HelpCircle, Edit, Calendar, MapPin, CreditCard, ChevronRight, Tag, LogIn } from 'lucide-react';
import { formatCpf, formatAddressForDisplay } from '../utils/brazilianUtils';
import LabelChip from '../components/labels/LabelChip';

const ProfilePage = () => {
  const { navigate } = useNavigation();
  const { user, logout, setProfile, isGuestUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Don't fetch profile for guests
    if (isGuestUser()) {
      setIsLoading(false);
      return;
    }

    const fetchProfile = async () => {
      setIsLoading(true);
      const profileData = await getUserProfile();
      if (profileData) {
        setProfile(profileData);
      }
      setIsLoading(false);
    };

    fetchProfile();
  }, [setProfile, isGuestUser]);

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  // Render guest CTA - Prompt to login/register
  const renderGuestCTA = () => (
    <div className="text-center py-20">
      <LogIn size={56} className="text-brand-dark/20 dark:text-brand-light/20 mx-auto mb-6" strokeWidth={1.5} />
      <h3 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light mb-3 tracking-wide">
        Faça login para acessar seu perfil
      </h3>
      <p className="text-brand-dark/60 dark:text-brand-light/60 mb-8 tracking-wide max-w-sm mx-auto">
        Crie uma conta para salvar suas preferências, ver seu histórico e muito mais.
      </p>
      <div className="flex flex-col space-y-3 max-w-xs mx-auto">
        <button
          onClick={() => navigate(ROUTES.LOGIN)}
          className="w-full px-8 py-4 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium text-sm tracking-widest uppercase hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
        >
          Entrar
        </button>
        <button
          onClick={() => navigate(ROUTES.REGISTER)}
          className="w-full px-8 py-4 bg-transparent text-brand-dark dark:text-brand-light border border-brand-dark/20 dark:border-brand-light/20 font-medium text-sm tracking-widest uppercase hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
        >
          Criar Conta
        </button>
      </div>
    </div>
  );

  // If user is a guest, show login CTA
  if (isGuestUser()) {
    return (
      <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto mobile-scroll">
            {/* Header - Luxury Design */}
            <div className="safe-area-top bg-brand-dark dark:bg-brand-light px-6 py-8 border-b border-brand-light/10 dark:border-brand-dark/10">
              <h1 className="text-3xl font-serif font-bold text-brand-light dark:text-brand-dark tracking-wide">Perfil</h1>
              <p className="text-brand-light/70 dark:text-brand-dark/70 text-sm tracking-wide mt-1">Suas informações e configurações</p>
            </div>

            {/* Guest CTA Content */}
            <div className="flex-1 bg-brand-light dark:bg-brand-dark px-6 py-8 min-h-0">
              {renderGuestCTA()}
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto mobile-scroll">
          {/* Header - Luxury Design */}
          <div className="safe-area-top bg-brand-dark dark:bg-brand-light px-6 py-8 border-b border-brand-light/10 dark:border-brand-dark/10">
            <h1 className="text-3xl font-serif font-bold text-brand-light dark:text-brand-dark tracking-wide">Perfil</h1>
            <p className="text-brand-light/70 dark:text-brand-dark/70 text-sm tracking-wide mt-1">Suas informações e configurações</p>
          </div>

          {/* Main Content */}
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center py-20">
              <p className="text-brand-dark dark:text-brand-light tracking-wider uppercase text-sm">Carregando...</p>
            </div>
          ) : (
            <div className="flex-1 bg-brand-light dark:bg-brand-dark px-6 py-8 min-h-0 pb-24">
              {/* User Info - Luxury Magazine Layout */}
              <div className="relative text-center mb-10 pt-4 pb-8 border-b border-brand-dark/10 dark:border-brand-light/10">
                <button
                  onClick={() => navigate(ROUTES.EDIT_PROFILE)}
                  className="absolute top-0 right-0 p-2.5 border border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
                >
                  <Edit size={18} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
                </button>
                <div className="w-24 h-24 rounded-full border-2 border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mx-auto mb-5 overflow-hidden">
                  {user?.photo_url ? (
                    <img
                      src={user.photo_url}
                      alt={user?.fullName || user?.name || 'Foto do perfil'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <User
                    size={40}
                    className="text-brand-dark dark:text-brand-light"
                    strokeWidth={1.5}
                    style={{ display: user?.photo_url ? 'none' : 'block' }}
                  />
                </div>
                <h2 className="text-2xl font-serif font-bold text-brand-dark dark:text-brand-light mb-2 tracking-wide">
                  {user?.fullName || user?.name || 'Usuário'}
                </h2>
                <p className="text-brand-dark/60 dark:text-brand-light/60 text-sm tracking-wide">{user?.email}</p>
              </div>

              {/* User Details - Luxury Grid Layout */}
              <div className="space-y-0 mb-8 border border-brand-dark/10 dark:border-brand-light/10">
                <div className="bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10 p-5">
                  <div className="flex items-center space-x-4">
                    <Mail size={18} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                    <div className="flex-1">
                      <p className="text-xs text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider mb-1">Email</p>
                      <p className="text-brand-dark dark:text-brand-light">{user?.email || 'Não informado'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10 p-5">
                  <div className="flex items-center space-x-4">
                    <Phone size={18} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                    <div className="flex-1">
                      <p className="text-xs text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider mb-1">Telefone</p>
                      <p className="text-brand-dark dark:text-brand-light">{user?.phone_number || 'Não informado'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10 p-5">
                  <div className="flex items-center space-x-4">
                    <User size={18} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                    <div className="flex-1">
                      <p className="text-xs text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider mb-1">Gênero</p>
                      <p className="text-brand-dark dark:text-brand-light capitalize">{user?.gender || 'Não informado'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10 p-5">
                  <div className="flex items-center space-x-4">
                    <Calendar size={18} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                    <div className="flex-1">
                      <p className="text-xs text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider mb-1">Data de Nascimento</p>
                      <p className="text-brand-dark dark:text-brand-light">{user?.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'Não informado'}</p>
                    </div>
                  </div>
                </div>

                {/* CPF Section */}
                <div className="bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10 p-5">
                  <div className="flex items-center space-x-4">
                    <CreditCard size={18} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                    <div className="flex-1">
                      <p className="text-xs text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider mb-1">CPF</p>
                      <p className="text-brand-dark dark:text-brand-light">{user?.cpf ? formatCpf(user.cpf) : 'Não informado'}</p>
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div className="bg-brand-light dark:bg-brand-dark p-5">
                  <div className="flex items-start space-x-4">
                    <MapPin size={18} className="text-brand-dark/40 dark:text-brand-light/40 mt-0.5" strokeWidth={1.5} />
                    <div className="flex-1">
                      <p className="text-xs text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider mb-2">Endereço</p>
                      {user?.address_street || user?.address_city || user?.address_cep ? (
                        <div className="text-brand-dark dark:text-brand-light text-sm leading-relaxed whitespace-pre-line">
                          {formatAddressForDisplay(user)}
                        </div>
                      ) : (
                        <p className="text-brand-dark dark:text-brand-light">Não informado</p>
                      )}
                    </div>
                  </div>
                </div>

              </div>

              {/* Labels Section */}
              {user?.labels && user.labels.length > 0 && (
                <div className="mb-8 border border-brand-dark/10 dark:border-brand-light/10 p-5">
                  <div className="flex items-start space-x-4">
                    <Tag size={18} className="text-brand-dark/40 dark:text-brand-light/40 mt-0.5" strokeWidth={1.5} />
                    <div className="flex-1">
                      <p className="text-xs text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider mb-3">Preferências</p>
                      <div className="flex flex-wrap gap-2">
                        {user.labels.map((label) => (
                          <LabelChip
                            key={label.id}
                            label={label}
                            size="medium"
                            showRemove={false}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Menu Options - Luxury Design */}
              <div className="space-y-0 mb-8 border border-brand-dark/10 dark:border-brand-light/10">
                <button className="w-full flex items-center justify-between p-5 bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300">
                  <div className="flex items-center space-x-4">
                    <Settings size={18} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                    <span className="text-brand-dark dark:text-brand-light tracking-wide">Configurações</span>
                  </div>
                  <ChevronRight size={18} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                </button>

                <button className="w-full flex items-center justify-between p-5 bg-brand-light dark:bg-brand-dark hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300">
                  <div className="flex items-center space-x-4">
                    <HelpCircle size={18} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                    <span className="text-brand-dark dark:text-brand-light tracking-wide">Ajuda e Suporte</span>
                  </div>
                  <ChevronRight size={18} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                </button>
              </div>

              {/* Logout Button - Luxury Design */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center space-x-3 p-5 bg-transparent border border-red-600 dark:border-red-400 text-red-600 dark:text-red-400 hover:bg-red-600 hover:dark:bg-red-400 hover:text-white hover:dark:text-black transition-all duration-300"
              >
                <LogOut size={18} strokeWidth={1.5} />
                <span className="font-medium tracking-wider uppercase text-sm">Sair da Conta</span>
              </button>
            </div>
          )}
        </div>
      </div>
      <BottomNavigation />
    </div>
  )
}

export default ProfilePage